import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function Dashboard() {
  const { user } = useAuth();
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAllData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [incRes, expRes] = await Promise.allSettled([api.get('/incomes'), api.get('/expenses')]);
      setIncomes(incRes.status === 'fulfilled' ? (incRes.value.data?.data || incRes.value.data || []) : []);
      setExpenses(expRes.status === 'fulfilled' ? (expRes.value.data?.data || expRes.value.data || []) : []);
    } catch (e) { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);
  const totalIncome = incomes.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const year = new Date().getFullYear();

  // Build monthly data from ALL records (handles 100+ records efficiently)
  const monthlyData = MONTHS.map((m, idx) => {
    const mn = idx + 1;
    return {
      month: m,
      income: incomes.filter(i => { const d = new Date(i.date); return d.getFullYear() === year && d.getMonth() + 1 === mn; }).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0),
      expenses: expenses.filter(e => { const d = new Date(e.date); return d.getFullYear() === year && d.getMonth() + 1 === mn; }).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
    };
  });

  const categoryData = expenses.reduce((acc, e) => {
    const cat = e.category || 'Other'; const ex = acc.find(a => a.name === cat);
    if (ex) ex.value += parseFloat(e.amount) || 0; else acc.push({ name: cat, value: parseFloat(e.amount) || 0 });
    return acc;
  }, []).slice(0, 8); // Limit to top 8 categories for performance

  // Recent transactions - limit to 5 for mobile
  const recentIncomes = [...incomes].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
  const recentExpenses = [...expenses].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

  if (loading) return <div className="flex justify-center items-center h-64 sm:h-96"><div className="text-center"><div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-indigo-600 mx-auto"></div><p className="mt-3 text-gray-500 text-sm">Loading...</p></div></div>;
  if (error) return <div className="text-center py-16 sm:py-20"><p className="text-red-500 mb-4">{error}</p><button onClick={loadAllData} className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm">Retry</button></div>;

  const hasData = incomes.length > 0 || expenses.length > 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Banner - Mobile responsive */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 sm:w-64 sm:h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <p className="text-indigo-200 text-xs sm:text-sm">Welcome back</p>
            <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold mt-0.5 sm:mt-1">{user?.username || user?.email?.split('@')[0] || 'User'} 👋</h1>
            <p className="text-indigo-200 text-xs sm:text-sm mt-1">Financial overview for {year}</p>
          </div>
          <div className="flex gap-2">
            <Link to="/income-tracker" className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium backdrop-blur-sm">+ Income</Link>
            <Link to="/expense-tracker" className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium backdrop-blur-sm">+ Expense</Link>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12 text-center">
          <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">📊</div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2">Welcome to FinovaTrack!</h2>
          <p className="text-gray-500 text-sm sm:text-base mb-4 sm:mb-6 max-w-md mx-auto">Start by adding your first income or expense.</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
            <Link to="/income-tracker" className="bg-green-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl text-sm font-medium hover:bg-green-700">Add Income →</Link>
            <Link to="/expense-tracker" className="bg-indigo-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl text-sm font-medium hover:bg-indigo-700">Track Expense →</Link>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Cards - 2x2 on mobile, 4 on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            {[
              { label: 'Balance', value: formatCurrency(totalIncome - totalExpenses), color: 'indigo', icon: '💰' },
              { label: 'Income', value: formatCurrency(totalIncome), color: 'green', icon: '📈' },
              { label: 'Expenses', value: formatCurrency(totalExpenses), color: 'red', icon: '📉' },
              { label: 'Total Records', value: incomes.length + expenses.length, color: 'purple', icon: '📊' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-1 sm:mb-2">
                  <span className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase">{s.label}</span>
                  <span className="text-sm sm:text-lg">{s.icon}</span>
                </div>
                <p className={`text-base sm:text-xl lg:text-2xl font-bold text-${s.color}-600 truncate`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Charts - Stack on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-4">📊 Monthly Overview {year}</h3>
              <div className="h-48 sm:h-72 lg:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000 ? (v/1000).toFixed(0) + 'k' : v} width={35} />
                    <Tooltip formatter={v => formatCurrency(v)} />
                    <Bar dataKey="income" fill="#10b981" radius={[3,3,0,0]} name="Income" />
                    <Bar dataKey="expenses" fill="#ef4444" radius={[3,3,0,0]} name="Expenses" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-4">🍩 Expense Categories</h3>
              {categoryData.length > 0 ? (
                <div className="h-48 sm:h-72 lg:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={categoryData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">{categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={v => formatCurrency(v)} /></PieChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="text-gray-400 text-center py-10 text-sm">No expenses yet</p>}
            </div>
          </div>

          {/* Recent Transactions - Mobile friendly cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-sm sm:text-lg font-semibold">Recent Income</h3>
                <Link to="/income-tracker" className="text-indigo-600 text-xs sm:text-sm font-medium">All →</Link>
              </div>
              {recentIncomes.length > 0 ? recentIncomes.map(inc => (
                <div key={inc._id} className="flex justify-between items-center py-2 sm:py-3 border-b border-gray-50 last:border-0">
                  <div><p className="font-medium text-gray-900 text-xs sm:text-sm">{inc.category}</p><p className="text-[10px] sm:text-xs text-gray-500">{new Date(inc.date).toLocaleDateString()}</p></div>
                  <p className="text-green-600 font-semibold text-xs sm:text-sm">{formatCurrency(inc.amount)}</p>
                </div>
              )) : <p className="text-gray-400 text-center py-6 text-xs sm:text-sm">No income yet</p>}
            </div>
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-sm sm:text-lg font-semibold">Recent Expenses</h3>
                <Link to="/expense-tracker" className="text-indigo-600 text-xs sm:text-sm font-medium">All →</Link>
              </div>
              {recentExpenses.length > 0 ? recentExpenses.map(exp => (
                <div key={exp._id} className="flex justify-between items-center py-2 sm:py-3 border-b border-gray-50 last:border-0">
                  <div><p className="font-medium text-gray-900 text-xs sm:text-sm">{exp.category}</p><p className="text-[10px] sm:text-xs text-gray-500">{new Date(exp.date).toLocaleDateString()}</p></div>
                  <p className="text-red-600 font-semibold text-xs sm:text-sm">{formatCurrency(exp.amount)}</p>
                </div>
              )) : <p className="text-gray-400 text-center py-6 text-xs sm:text-sm">No expenses yet</p>}
            </div>
          </div>

          {/* Quick Actions - Mobile grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            {[
              { to: '/income-tracker', icon: '💰', label: 'Income' },
              { to: '/expense-tracker', icon: '💸', label: 'Expenses' },
              { to: '/budget-planner', icon: '📊', label: 'Budget' },
              { to: '/savings-goals', icon: '🎯', label: 'Goals' },
              { to: '/net-worth', icon: '💎', label: 'Net Worth' },
              { to: '/monthly-dashboard', icon: '📅', label: 'Annual' },
            ].map((item, i) => (
              <Link key={i} to={item.to} className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-2 sm:p-4 text-center hover:shadow-md transition-all active:scale-95">
                <span className="text-lg sm:text-xl">{item.icon}</span>
                <p className="text-[10px] sm:text-sm font-medium text-gray-900 mt-1">{item.label}</p>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}